<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { library } from '@common/library/library';
import { componentStore } from "@common/componentStore/componentStore";
import backdrop from "@common/backdrop/backdrop";
import { GenerateComponent } from "@common/activityStepPanel/GenerateComponent";

const editorSettings = {
    templateSettingsPanel: {
        type: 'panel',
        label: 'Header & Style Settings',
        id: 'templateSettingsPanel',
        fields: [
            'headingTemplate',
            'componentMargin',
            'customComponentMargin',
            'componentPadding',
            'customComponentPadding',
            'borderRadius',
            'textColor',
            'backgroundColor',
            'borderColor'
        ]
    },
    messageSettingsPanel: {
        type: 'panel',
        label: 'Message & Style Settings',
        id: 'messageSettingsPanel',
        fields: [
            'customMessageTemplate',
            'messageStylesSameAsComponent',
            'messageMargin',
            'customMessageMargin',
            'messagePadding',
            'customMessagePadding',
            'messageBorderRadius',
            'messageTextColor',
            'messageBackgroundColor',
            'messageBorderColor'
        ]
    },
    imageSettingsPanel: {
        type: 'panel',
        label: 'Image Settings',
        id: 'imageSettingsPanel',
        fields: [
            'backdropImageNotice',
            'messageImage',
            'messageImageWidth',
            'messageImageAlignment',
        ]
    },
    displaySettingsPanel: {
        type: 'panel',
        label: 'Display Settings',
        id: 'displaySettingsPanel',
        fields: [
            'allowDismiss',
            'dismissStyle',
            'showAsToast',
            'toastInitialText',
            'toastWidth'
        ]
    },
    backdropSettingsPanel: {
        type: 'panel',
        label: 'Backdrop Settings',
        id: 'backdropSettingsPanel',
        fields: [
            'backdropType',
            'backdropMinHeight',
            'backgroundImage'
        ]
    },
    headingTemplate: {
        type: 'text',
        label: 'Header Template',
        id: 'headingTemplate',
        default: '<h1>A heading</h1>',
        helpText: 'HTML for the title template.'
    },
    backgroundColor: {
        type: 'color',
        label: 'Component Background Color',
        id: 'backgroundColor',
        default: '#ffffffff'
    },
    borderRadius: {
        type: 'string',
        label: 'Border Radius',
        id: 'borderRadius',
        default: '0',
        helpText: 'eg: 4px',
    },
    borderColor: {
        type: 'color',
        label: 'Component Border Color',
        id: 'borderColor',
        default: '#cacacaff'
    },
    textColor: {
        type: 'color',
        label: 'Component Text Color',
        id: 'textColor',
        default: '#000000ff'
    },
    componentMargin: {
        type: 'dropdown',
        label: 'Component Margin',
        id: 'componentMargin',
        helpText: 'Add a margin around the message panel.',
        items: [
            '0',
            '5px',
            '10px',
            '15px',
            '20px',
            '25px',
            '30px',
            'custom'
        ],
        default: '10px'
    },
    customComponentMargin: {
        type: 'string',
        label: 'Custom Component Margin',
        id: 'customComponentMargin',
        helpText: 'Custom margin is added by comma separated values for: top right bottom left',
        message: 'eg: 5px 10px 10px 50px or 1rem 2rem',
        default: '',
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: 'this.valuesMap.componentMargin === "custom"'
            }
        ],
    },
    componentPadding: {
        type: 'dropdown',
        label: 'Component Padding',
        id: 'componentPadding',
        helpText: 'Add a padding around the message panel.',
        items: [
            '0',
            '5px',
            '10px',
            '15px',
            '20px',
            '25px',
            '30px',
            'custom'
        ],
        default: '10px'
    },
    customComponentPadding: {
        type: 'string',
        label: 'Custom Component Padding',
        id: 'customComponentPadding',
        helpText: 'Custom padding is added by comma separated values for: top right bottom left',
        message: 'eg: 5px 10px 10px 50px or 1rem 2rem',
        default: '',
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: 'this.valuesMap.componentPadding === "custom"'
            }
        ],
    },
    customMessageTemplate: {
        type: 'text',
        label: 'Message Template',
        id: 'customMessageTemplate',
        default: '<p>A message.</p>',
        helpText: 'Provide a custom message using HTML and autosubscribe fields inside double curly braces eg: {{records.vol[volId].VOL_FIRST_NAME}} or records.vol[\'CA#UDF Group/UDF\'].'
    },
    messageStylesSameAsComponent: {
        type: 'boolean',
        label: 'Keep Global Component Styles for Message (in Template Settings)',
        id: 'messageStylesSameAsComponent',
        default: true
    },
    messageMargin: {
        type: 'dropdown',
        label: 'Message Margin',
        id: 'messageMargin',
        helpText: 'Add a margin around the message panel.',
        items: [
            '0',
            '5px',
            '10px',
            '15px',
            '20px',
            '25px',
            '30px',
            'custom'
        ],
        default: '10px'
    },
    customMessageMargin: {
        type: 'string',
        label: 'Custom Margin',
        id: 'customMessageMargin',
        helpText: 'Custom margin is added by comma separated values for: top right bottom left',
        message: 'eg: 5px 10px 10px 50px or 1rem 2rem',
        default: '',
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: 'this.valuesMap.messageMargin === "custom"'
            }
        ],
    },
    messagePadding: {
        type: 'dropdown',
        label: 'Message Padding',
        id: 'messagePadding',
        helpText: 'Add a padding around the message panel.',
        items: [
            '0',
            '5px',
            '10px',
            '15px',
            '20px',
            '25px',
            '30px',
            'custom'
        ],
        default: '10px'
    },
    customMessagePadding: {
        type: 'string',
        label: 'Custom Padding',
        id: 'customMessagePadding',
        helpText: 'Custom padding is added by comma separated values for: top right bottom left',
        message: 'eg: 5px 10px 10px 50px or 1rem 2rem',
        default: '',
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: 'this.valuesMap.messagePadding === "custom"'
            }
        ],
    },
    messageBackgroundColor: {
        type: 'color',
        label: 'Message Background Color',
        id: 'messageBackgroundColor',
        default: '#ffffffff',
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: '!this.valuesMap.messageStylesSameAsComponent'
            }
        ],
    },
    messageBorderColor: {
        type: 'color',
        label: 'Message Border Color',
        id: 'messageBorderColor',
        default: '#cacacaff',
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: '!this.valuesMap.messageStylesSameAsComponent'
            }
        ],
    },
    messageBorderRadius: {
        type: 'string',
        label: 'Message Border Radius',
        id: 'messageBorderRadius',
        default: '0',
        helpText: 'eg: 4px',
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: '!this.valuesMap.messageStylesSameAsComponent'
            }
        ],
    },
    messageTextColor: {
        type: 'color',
        label: 'Message Text Color',
        id: 'textColor',
        default: '#000000ff',
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: '!this.valuesMap.messageStylesSameAsComponent'
            }
        ],
    },
    backdropType: {
        type: 'dropdown',
        label: 'Backdrop Type',
        id: 'backdropType',
        items: [
            'sunrise',
            'white void'
        ],
        helpText: 'Choose an animation, image, color or the invigorating "white void".'
    },
    backgroundImage: {
        type: 'upload',
        label: 'Backdrop Image',
        id: 'backgroundImage',
        allowedExtensions: '.png, .jpg, .jpeg',
        showAsDefaultInput: true,
        multiple: false,
        psp: 'component',
        helpText: 'Add an image to display as the background of the message panel.'
    },
    backdropImageNotice: {
        type: 'alert',
        text: '<strong>Note:</strong> If you want to set the background image, go to backdrop settings.'
    },
    messageImage: {
        type: 'upload',
        label: 'Message Image',
        id: 'messageImage',
        allowedExtensions: '.png, .jpg, .jpeg',
        showAsDefaultInput: true,
        multiple: false,
        psp: 'component',
        helpText: 'Add an image to display above the message template.'
    },
    messageImageWidth: {
        type: 'string',
        label: 'Message Image Width',
        id: 'messageImageWidth',
        default: '100%',
        message: 'Include a unit such as %, px, em  eg: 150px',
        helpText: 'Specify the width of the image.  The height of the image will be determined by the image\'s original aspect ratio.'
    },
    messageImageAlignment: {
        type: 'dropdown',
        label: 'Message Image Alignment',
        id: 'messageImageAlignment',
        items: ['left', 'right', 'center'],
        default: 'left',
        helpText: 'Specify how the image should be aligned if it does not use the full width of the available space.'
    },
    backdropMinHeight: {
        type: 'string',
        label: 'Backdrop Minimum Height',
        id: 'backdropMinHeight',
        message: 'eg: 300px or 40% or 50vh',
        helpText: 'Percentage height is based on the height of the parent element. vh height is a percentage based on the total window height.'
    },
    allowDismiss: {
        type: 'boolean',
        label: 'Dismissible',
        helpText: 'Allow the message to be dismissible. This is only available when the field is part of a user profile.',
        default: false
    },
    dismissStyle: {
        type: 'dropdown',
        label: 'Dismiss Button Style',
        id: 'dismissStyle',
        items: [
            'text',
            'closeicon',
        ],
        default: 'text'
    },
    showAsToast: {
        type: 'boolean',
        label: 'Show the message initially as a toast.',
        id: 'showAsTost',
        default: false
    },
    toastInitialText: {
        type: 'text',
        label: 'Toast Short Text',
        id: 'toastInitialText',
        default: false
    },
    toastWidth: {
        type: 'string',
        label: 'Toast Width',
        id: 'toastWidth'
    }
};

const messagePanelConfig = {
    cId: "messagePanel",
    cType: "messagePanel",
    cTitle: "Message Panel",
    cTemplate: `<div v-if="messagesToShow">
    <div v-if="config.showAsToast" :id="'toastMessageContainer_' + config.cId" class="message-panel-toast-container">
        <div class="message-panel-outer" :style="messagePanelStyles">
            <div v-if="!showFullToastMsg">
                <div class="toast-initial-text" v-html="config.toastInitialText"></div>
                <button type="button" class="more-button" @click="showFullToastMsg = true">read more</button>
                <div class="message-item-button-area" v-if="config.allowDismiss">
                    <control v-if="config.dismissStyle == 'closeicon'" name="CLOSE_MESSAGES_ICON"/>
                    <control v-else name="CLOSE_MESSAGES"/>
                </div>
            </div>
            <div v-if="showFullToastMsg">
                <component :is="headerTemplate.cId" :key="headerTemplate.cId" class="message-header"></component>
                <div class="message-container" :style="messageStyles">
                    <div v-if="config.messageImage" :style="{ 'text-align': config.messageImageAlignment }">
                        <img class="message-image" :style="{ width: config.messageImageWidth }" :src="config.messageImage"/>
                    </div>
                    <div class="message-item" v-for="(message, index) in messages" :style="messageStyles">
                        <div class="message-item-text" v-html="message.text"></div>
                        <div class="message-item-button-area" v-if="config.allowDismiss && message.dismissible">
                            <control v-if="config.dismissStyle == 'closeicon'" name="DISMISS_ICON" :actionParams="index"/>
                            <control v-else name="DISMISS" :actionParams="index"/>
                        </div>
                    </div>
                    <div class="custom-message-container message-item" v-if="config.customMessageTemplate && displayCustomMessage && !messages.length">
                        <div class="message-item-text">
                            <component :is="messageTemplate.cId" :key="messageTemplate.cId"></component>
                        </div>
                        <div class="message-item-button-area" v-if="config.allowDismiss">
                            <control v-if="config.dismissStyle == 'closeicon'" name="CLOSE_MESSAGES_ICON"/>
                            <control v-else name="CLOSE_MESSAGES"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-else class="message-panel-inline-container">
        <backdrop :backdrop-type="config.backdropType" :backdrop-min-height="config.backdropMinHeight">
            <div class="message-panel-outer" :style="messagePanelStyles">
                <component :is="headerTemplate.cId" :key="headerTemplate.cId" class="message-header"></component>
                <div class="message-container" :style="messageStyles">
                    <div v-if="config.messageImage" >
                        <img class="message-image" :style="{ width: config.messageImageWidth }" :src="config.messageImage"/>
                    </div>
                    <div class="message-item" v-for="(message, index) in messages">
                        <div class="message-item-text" v-html="message.text"></div>
                        <div class="message-item-button-area" v-if="config.allowDismiss && message.dismissible">
                            <control v-if="config.dismissStyle == 'closeicon'" name="DISMISS_ICON" :actionParams="index"/>
                            <control v-else name="DISMISS" :actionParams="index"/>
                        </div>
                    </div>
                    <div class="custom-message-container message-item" v-if="config.customMessageTemplate && displayCustomMessage && !messages.length">
                        <div class="message-item-text">
                            <component :is="messageTemplate.cId" :key="messageTemplate.cId"></component>
                        </div>
                        <div class="message-item-button-area" v-if="config.allowDismiss">
                            <control v-if="config.dismissStyle == 'closeicon'" name="CLOSE_MESSAGES_ICON"/>
                            <control v-else name="CLOSE_MESSAGES"/>
                        </div>
                    </div>
                </div>
            </div>
        </backdrop>
    </div>
</div>`,
    controls: [
        {
            name: 'DISMISS',
            type: 'button',
            label: 'Dismiss',
            onclick: 'dismissMessage',
            text: true,
            cssClass: 'e-flat'
        }, {
            name: 'CLOSE_MESSAGES',
            type: 'button',
            label: 'Dismiss',
            onclick: 'closeCustomMessage',
            text: true,
            cssClass: 'e-flat'
        }, {
            name: 'DISMISS_ICON',
            type: 'button',
            label: 'Dismiss',
            onclick: 'dismissMessage',
            hideLabel: true,
            iconCss: 'e-icons e-close dismiss-message_panel',
            text: true,
            cssClass: 'e-flat'
        }, {
            name: 'CLOSE_MESSAGES_ICON',
            type: 'button',
            label: 'Dismiss',
            onclick: 'closeCustomMessage',
            hideLabel: true,
            iconCss: 'e-icons e-close dismiss-message_panel',
            text: true,
            cssClass: 'e-flat'
        }
    ]
};

/* Helper: compute styles */
function computePanelStyle(config, prefix = '') {
    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
    const marginKey = prefix ? `${prefix}Margin` : 'componentMargin';
    const paddingKey = prefix ? `${prefix}Padding` : 'componentPadding';
    const customMarginKey = prefix ? `custom${capitalize(prefix)}Margin` : 'customComponentMargin';
    const customPaddingKey = prefix ? `custom${capitalize(prefix)}Padding` : 'customComponentPadding';
    const backgroundKey = prefix ? `${prefix}BackgroundColor` : 'backgroundColor';
    const borderRadiusKey = prefix ? `${prefix}BorderRadius` : 'borderRadius';
    const textColorKey = prefix ? `${prefix}TextColor` : 'textColor';
    const borderColorKey = prefix ? `${prefix}BorderColor` : 'borderColor';
    return {
        backgroundColor: config[backgroundKey] ?? '#ffffff',
        borderRadius: config[borderRadiusKey] ?? '0',
        color: config[textColorKey] ?? '#000000',
        margin: config[marginKey] === 'custom'
            ? config[customMarginKey]
            : config[marginKey],
        padding: config[paddingKey] === 'custom'
            ? config[customPaddingKey]
            : config[paddingKey],
        border: `1px solid ${config[borderColorKey] ?? '#000000'}`
    };
}

const messagePanelCmp = {
    name: "messagePanel",
    editorSettings: editorSettings,
    data() {
        return {
            messageToast: false,
            messages: [],
            displayCustomMessage: false,
            showFullToastMsg: false
        };
    },
    computed: {
        headerTemplate() {
            return new GenerateComponent(this, this.config?.headingTemplate, 'headerTemplate');
        },
        messageTemplate() {
            return new GenerateComponent(this, this.config?.customMessageTemplate, 'messageTemplate');
        },
        messagePanelStyles() {
            return computePanelStyle(this.config);
        },
        messageStyles() {
            return !this.separateStyles && !this.config.turnOffStyles
                ? computePanelStyle(this.config, 'message')
                : {};
        },
        separateStyles() {
            return !this.config.turnOffStyles ? this.config.messageStylesSameAsComponent : {};
        },
        messagesToShow() {
            return this.displayCustomMessage || this.messages.length > 0;
        }
    },
    methods: {
        onLoad() {
            if (this.config.showAsToast && this.config.toastInitialText) {
                this.messageToast = library.showToast(false, {
                    templateElemId: '#toastMessageContainer_' + this.config.cId,
                    timeout: 600,
                    width: this.config.toastWidth || '300px',
                    cssClass: 'message-panel-toast'
                });
            }
            if (this.config.customMessageTemplate && !this.messages.length) {
                this.displayCustomMessage = true;
            }
        },
        dismissMessage(event, thisControl, msgIndex) {
            this.messages.splice(msgIndex, 1);
            // TODO clear out the UDF field if dismissible
        },
        dismissMessagePanel() {
            this.$destroy();
            this.$el.parentNode.removeChild(this.$el);
        },
        closeCustomMessage() {
            if (this.messageToast) {
                this.messageToast.hide();
            }
            this.displayCustomMessage = false;
        }
    },
    components: {
        backdrop,
        "ejs-button": defineAsyncComponent(() => import('@common/syncfusion/button')),
    }
};

componentStore.initComponent(messagePanelCmp, messagePanelConfig, '@common/messagePanel.vue');

export default messagePanelCmp;
</script>

<style scoped>
    .custom-message-container {
        padding: 1rem 0;
        width: fit-content;
    }
    .message-panel-toast-container .message-item {
        background: transparent;
        border: 0 none;
        margin-bottom: 0;
    }
    .message-panel-inline-container .message-item {
        position: relative;
    }
    .message-item-text {
        flex-grow: 9;
        font-size: 16px;
    }
    .message-item-button-area .e-control {
        display: inline-block;
    }
    .message-image {
        display: inline-block;
    }
    .message-panel-toast-container {
        width: 100%;
    }
    .message-panel-toast-container .toast-initial-text {
        margin: 10px 0 0 0;
    }
    .message-panel-toast-container .message-panel-outer {
        padding: 10px;
    }
    .message-panel-toast-container .message-item-button-area,
    .message-item-button-area {
        position: absolute;
        top: 0;
        right: 0;
    }
    .more-button {
        text-decoration: underline;
        border: 0;
        padding: 5px;
        margin: 0;
        background: transparent;
    }
</style>
